import { INPUT_AUTOCOMPLETE, INPUT_TYPE, PAYMENT_TYPES } from '@nandosaus/constants';
import { isEmpty, trim } from 'lodash';
import { observer } from 'mobx-react';
import { useField } from 'formik';
import { useRootStore } from '@nandosaus/state-management';
import React from 'react';

import { Alert } from '../../../alert';
import { Box, Col, Flex, Row } from '../../../grid';
import { Button } from '../../../button';
import { FormikTextInput } from '../../../input';
import { LoadingIndicator } from '../../../icons/loading-indicator';

const GiftCardEntryForm = observer(() => {
  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState();
  const { OrderStore, CartStore } = useRootStore();
  const [cardNumber, cardNumberMeta, cardNumberHelper] = useField('giftCardNumber');
  const [verificationCode, verificationCodeMeta, verificationCodeHelper] = useField('giftCardVerificationCode');
  const { setGiftCard } = OrderStore.payment;

  const valid = cardNumberMeta.error === undefined && verificationCodeMeta.error === undefined;

  const handleAddCard = async () => {
    if (isEmpty(trim(verificationCode.value)) || isEmpty(trim(cardNumber.value))) {
      setErrorMessage('Please enter a gift card number and verify code');
      return;
    }

    setLoading(true);

    let success;

    try {
      success = await setGiftCard(cardNumber.value, verificationCode.value);
    } catch (error) {
      setErrorMessage('Sorry, a problem occurred whilst verifying the gift card details. Please try again.');
      setLoading(false);
      return;
    }

    setLoading(false);

    if (success) {
      CartStore.setSplitPaymentType(PAYMENT_TYPES.GIFT_CARD);
      setErrorMessage(undefined);
      cardNumberHelper.setValue('', false);
      cardNumberHelper.setTouched(false);
      verificationCodeHelper.setValue('', false);
      verificationCodeHelper.setTouched(false);
    } else {
      setErrorMessage('The gift card details you entered are invalid, please check them and try again.');
    }
  };

  return (
    <>
      <Flex border={1} borderColor="grey100" borderStyle="solid" flexDirection="column" p="1.5rem" mb={1}>
        <Row flexWrap="wrap">
          <Col mb={{ xs: 1, lg: 0 }} width={{ xs: 12 / 12, lg: 5 / 12 }}>
            <FormikTextInput
              autoComplete={INPUT_AUTOCOMPLETE.TEXT}
              disabled={loading}
              label="Gift Card Number"
              maxLength={11}
              name={cardNumber.name}
              placeholder="Include letters and numbers"
              type={INPUT_TYPE.TEXT}
              value={cardNumber.value}
            />
          </Col>
          <Col width={{ xs: 6 / 12, lg: 3 / 12 }}>
            <FormikTextInput
              autoComplete={INPUT_AUTOCOMPLETE.TEXT}
              disabled={loading}
              label="Verify Code"
              maxLength={6}
              name={verificationCode.name}
              type={INPUT_TYPE.TEXT}
            />
          </Col>
          <Col width={{ xs: 12 / 12, lg: 4 / 12 }}>
            <Button
              disabled={!valid || loading}
              height={57}
              mt={{ xs: '1.5rem', lg: '25px' }}
              onClick={handleAddCard}
              title="Save"
              type="button"
              variant="secondary"
            >
              {loading ? <LoadingIndicator /> : 'Apply'}
            </Button>
          </Col>
        </Row>
        {errorMessage !== undefined && (
          <Box mt={1}>
            <Alert type="error">{errorMessage}</Alert>
          </Box>
        )}
      </Flex>
    </>
  );
});

export { GiftCardEntryForm };
